import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates as batch } from 'react-dom';
import { getLocationAndSave } from '../../utils/locationUtil';

export const PLAYER_MAP_LOCATION_FRESH_MS = 60000;
const noop = () => {};
const initialState = () => ({ busy: false, fix: null, error: '', saveFailed: false, browsing: false });
const visible = () => document.visibilityState !== 'hidden';
const validPosition = (position) => {
  const { latitude, longitude, accuracy } = position?.coords || {};
  return Number.isFinite(latitude) && Math.abs(latitude) <= 90 &&
    Number.isFinite(longitude) && Math.abs(longitude) <= 180 &&
    Number.isFinite(accuracy) && accuracy >= 0 &&
    Number.isFinite(position?.timestamp) && position.timestamp > 0 &&
    position.timestamp <= Date.now();
};

// Owns only bounded map-entry/recenter requests. The existing live movement
// and save-heartbeat effects remain independent and unchanged.
export default function usePlayerMapLocation({ player, onFix }) {
  const playerKey = player?.data?.player?._id || player?._id || '';
  const ownerKey = playerKey && player?.token ? `${playerKey}:${player.token}` : '';
  const activeKey = useRef(ownerKey);
  activeKey.current = ownerKey;
  const inputs = useRef({ player, onFix });
  inputs.current = { player, onFix };
  const ownerRef = useRef(null);
  const [state, setState] = useState(initialState);

  const browseFirst = useCallback(() => {
    const owner = ownerRef.current;
    if (!owner) return;
    owner.browsing = true;
    owner.generation += 1;
    owner.request = null;
    setState((prev) => ({ ...prev, busy: false, error: '', browsing: true }));
  }, []);

  const refresh = useCallback(({ automatic = false, entry = false, hooks = {} } = {}) => {
    const owner = ownerRef.current;
    if (!owner?.valid || !owner.key || activeKey.current !== owner.key) return Promise.resolve(null);
    if (automatic && (!visible() || owner.browsing || owner.denied)) return Promise.resolve(null);
    if (!automatic) {
      owner.browsing = false;
      owner.denied = false;
    }
    if (owner.request) {
      if (!automatic) {
        owner.request.manual = true;
        owner.request.hooks = hooks;
      }
      return owner.request.promise;
    }
    const now = Date.now();
    if (automatic && !entry && (
      now - owner.lastFixAt < PLAYER_MAP_LOCATION_FRESH_MS ||
      now - owner.lastAttemptAt < PLAYER_MAP_LOCATION_FRESH_MS
    )) return Promise.resolve(null);
    owner.lastAttemptAt = now;
    const request = { generation: ++owner.generation, manual: !automatic, hooks };
    owner.request = request;
    const isOwned = () => owner.valid && ownerRef.current === owner &&
      activeKey.current === owner.key && owner.generation === request.generation && !owner.browsing;
    const isCurrent = () => isOwned() && owner.permission?.state !== 'denied' &&
      (request.manual || visible());
    setState((prev) => ({ ...prev, busy: true, error: '', saveFailed: false, browsing: false }));

    request.promise = (async () => {
      let saveFailed = false;
      try {
        let permission = null;
        try {
          permission = await navigator.permissions?.query({ name: 'geolocation' });
        } catch (err) { /* Unknown permission requires an explicit action. */ }
        if (!isOwned()) return null;
        owner.removePermissionListener?.();
        owner.permission = permission;
        const permissionChanged = () => {
          if (!owner.valid || ownerRef.current !== owner || activeKey.current !== owner.key) return;
          owner.denied = permission.state === 'denied';
          if (permission.state !== 'denied') return;
          owner.generation += 1;
          owner.request = null;
          setState((prev) => ({ ...prev, busy: false, error: 'denied' }));
        };
        permission?.addEventListener?.('change', permissionChanged);
        owner.removePermissionListener = () => permission?.removeEventListener?.('change', permissionChanged);
        if (permission?.state === 'denied') {
          owner.denied = true;
          setState((prev) => ({ ...prev, busy: false, error: 'denied' }));
          if (request.manual) throw Object.assign(new Error('Location access is blocked.'), { code: 1 });
          return null;
        }
        if (!request.manual && (permission?.state !== 'granted' || !visible())) return null;
        if (!navigator.geolocation?.getCurrentPosition) throw Object.assign(new Error(), { code: 2 });
        const position = await getLocationAndSave(noop, noop, noop, noop, inputs.current.player, {
          isCurrent: (candidate) => isCurrent() && (!candidate || validPosition(candidate)),
          onPermissionGranted: (fix) => {
            if (!isCurrent()) return;
            owner.lastFixAt = fix.timestamp;
            owner.lastFix = fix;
            batch(() => {
              setState((prev) => ({ ...prev, fix }));
              inputs.current.onFix(fix, { manual: request.manual, isCurrent });
            });
            if (isCurrent()) request.hooks.onPermissionGranted?.(fix);
          },
          onSaveAttempted: (fix) => {
            if (isCurrent()) request.hooks.onSaveAttempted?.(fix, permission?.state || 'unknown');
          },
          onSaved: (fix) => {
            if (isCurrent()) request.hooks.onSaved?.(fix);
          },
          onSaveFailed: (err) => {
            if (!isCurrent()) return;
            saveFailed = true;
            request.hooks.onSaveFailed?.(err);
          },
        });
        if (!isCurrent()) return null;
        if (!position) throw Object.assign(new Error(), { code: 2 });
        setState((prev) => ({ ...prev, saveFailed }));
        return { position, isCurrent, permissionState: permission?.state || 'unknown' };
      } catch (err) {
        if (!isOwned()) return null;
        const error = err?.code === 1 ? 'denied' : err?.code === 3 ? 'timeout' : 'unavailable';
        if (error === 'denied') owner.denied = true;
        setState((prev) => ({ ...prev, error }));
        if (request.manual) throw err;
        return null;
      } finally {
        if (owner.request === request) {
          owner.request = null;
          if (!request.manual && !visible()) owner.lastAttemptAt = 0;
          if (owner.valid && ownerRef.current === owner && activeKey.current === owner.key) {
            setState((prev) => ({ ...prev, busy: false }));
          }
        }
      }
    })();
    return request.promise;
  }, []);

  useLayoutEffect(() => {
    const owner = { key: ownerKey, valid: true, generation: 0, browsing: false,
      request: null, permission: null, lastFixAt: 0, lastAttemptAt: 0 };
    ownerRef.current = owner;
    setState(initialState());
    return () => {
      owner.valid = false;
      owner.removePermissionListener?.();
    };
  }, [ownerKey]);

  useEffect(() => {
    const foreground = () => { if (visible()) void refresh({ automatic: true }); };
    void refresh({ automatic: true, entry: true });
    window.addEventListener('focus', foreground);
    document.addEventListener('visibilitychange', foreground);
    return () => {
      window.removeEventListener('focus', foreground);
      document.removeEventListener('visibilitychange', foreground);
    };
  }, [ownerKey, refresh]);

  const captureAuthority = useCallback(() => {
    const owner = ownerRef.current;
    const fix = owner?.lastFix;
    return () => !!owner?.valid && ownerRef.current === owner &&
      activeKey.current === owner.key && owner.lastFix === fix;
  }, []);

  return { ...state, refresh, browseFirst, captureAuthority };
}
